import { AxiosResponse } from 'axios';

import { OrganizationInfo } from '../api/api';
import { Nullable } from '../types/types';
import { calculateTotalPrice } from '../utils/calculatePrice';
import camelCaseKeysDeep from '../utils/camelCaseKeys';

import { CartItem, PricingFormCartItem, ServiceCartItem, State, TripCharge } from './types';

export const isServiceCartItem = (item: CartItem): item is ServiceCartItem => item.type === 'service';
export const isPricingFormItem = (item: CartItem): item is PricingFormCartItem => item.type === 'pricing-form';

export const hasAtLeastOnePricingForm = (cart: CartItem[]) => cart.some((item: CartItem) => isPricingFormItem(item));

export const hasAtLeastOneEstimate = (cart: CartItem[]) =>
  cart.some(
    (item: CartItem) =>
      (isServiceCartItem(item) && item.service.bookableAs === 'estimate') ||
      (isPricingFormItem(item) && item.pricingForm.bookableAs === 'estimate')
  );

export const hasPaymentStep = (cart: CartItem[], orgData: OrganizationInfo, tripCharge: Nullable<TripCharge>) => {
  const hasPricingForm = hasAtLeastOnePricingForm(cart);
  const pricingFormsHaveBookableAs = cart
    .filter(isPricingFormItem)
    .some((item: PricingFormCartItem) => Boolean(item.pricingForm.bookableAs));

  const hasEstimate = hasAtLeastOneEstimate(cart);
  const total = calculateTotalPrice(cart, orgData?.taxInfo?.rate || null) + (tripCharge?.tripChargeFee || 0);
  const paymentEnabled = orgData?.paymentsRequired || false;

  if (total <= 0 || !paymentEnabled) {
    return false;
  }

  const oldLogicForPaymentStep = hasPricingForm || !hasEstimate;
  const newLogicForPaymentStep = !hasEstimate;

  // If pricing forms have bookableAs, we use the new logic for payment step
  // Otherwise, we use the old logic which is based on the presence of pricing forms
  // Pricing form in the cart is equal for payment step (in the old logic)
  return pricingFormsHaveBookableAs ? newLogicForPaymentStep : oldLogicForPaymentStep;
};

export const getItemFromCart = (cartItem: CartItem) =>
  isServiceCartItem(cartItem) ? cartItem.service : cartItem.pricingForm;

export const getQuantityFromItem = (item: CartItem) => {
  return isServiceCartItem(item) ? item.quantity : 1;
};

export const getServiceItemsFromCart = (state: State) => {
  return state.cart.filter(isServiceCartItem) as ServiceCartItem[];
};

export const getPricingFormItemsFromCart = (state: State) => {
  return state.cart.filter(isPricingFormItem) as PricingFormCartItem[];
};

export const findCartItem = (cart: CartItem[], uuid: string) =>
  cart.find((item: CartItem) => getItemFromCart(item).uuid === uuid);

/* eslint-disable no-template-curly-in-string */
export const prepareAWSKey = (fileName: string, key: string): string => key.replace('${filename}', fileName);

export const mapResponseToCamelCase = <T>(response: AxiosResponse<T>) => ({
  ...response,
  ...(response?.data ? { data: camelCaseKeysDeep(response.data) } : {}),
});
